import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import DocumentLink from 'src/components/DocumentLink/DocumentLink';
import DetailRow from 'src/components/DetailRow';
import { DocumentType, AutomaticDebitMenuActions } from 'src/utils/constants/clients';
import DocumentsMenu from 'src/components/DocumentsMenu/DocumentsMenu';
import MoreMenu from 'src/components/MoreMenu/MoreMenu';
import { Box } from '@mui/material';
import ContractStatus, { AutomaticDebitRequestSignStatus } from 'src/utils/constants/contracts';
import {
  getAmendingAgreementSignStatus,
  getAutomaticDebitRequestSignStatus,
  getContractSignedStatus,
  getContractCessionSignStatus,
  getContractEarlyTerminationSignStatus,
  getOnSendToSignHandler,
  shouldShowSendToSignAutomaticDebitRequest
} from 'src/utils/contract';
import getPromissoryNoteSignStatus from 'src/utils/promissoryNote';
import ConfirmationDialog from 'src/components/ConfirmDialog/ConfirmationDialog';
import { useDispatch, useSelector } from 'react-redux';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import {
  SEND_TO_SIGN_AUTOMATIC_REQUEST_DOCUMENT,
  sendToSignAutomaticRequestDocumentAction
} from 'src/store/actions/contractActions';
import { Send as SendIcon, ListAlt as ListAltIcon } from '@mui/icons-material';
import DocumentListModal from 'src/components/DocumentListModal';
import DocumentsContractMenu from 'src/views/contract/ContractDetailView/DocumentContractMenu/DocumentContractMenu';
import useStyles from '../../styles';
import TerminationLetterDocumentMenu from './TerminationLetterDocumentMenu/TerminationLetterDocumentMenu';

const DocumentDetail = ({ contract }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [openConfirmationDialog, sentOpenConfirmationDialog] = useState(false);
  const [openAutomaticDebitHistory, setOpenAutomaticDebitHistory] = useState(null);
  const isLoading = useSelector(
    isLoadingSelector([SEND_TO_SIGN_AUTOMATIC_REQUEST_DOCUMENT.action])
  );
  const dispatch = useDispatch();

  const handleCloseAutomaticDebitRequestDialog = () => {
    sentOpenConfirmationDialog(false);
  };

  const sendToSignAutomaticDebitRequest = () => {
    dispatch(
      sendToSignAutomaticRequestDocumentAction(
        contract.id,
        handleCloseAutomaticDebitRequestDialog
      )
    );
  };

  const handleSendToSignAutomaticDebitRequest = () => {
    sentOpenConfirmationDialog(true);
  };

  const handleOpenActionsAutomaticDebitRequest = optionName => {
    switch (optionName) {
      case AutomaticDebitMenuActions.SEND_TO_SIGN_AUTOMATIC_DEBIT_REQUEST:
        if (getOnSendToSignHandler(contract, handleSendToSignAutomaticDebitRequest)) {
          handleSendToSignAutomaticDebitRequest();
        }
        break;
      case AutomaticDebitMenuActions.HISTORY_AUTOMATIC_DEBIT_REQUEST:
        setOpenAutomaticDebitHistory(DocumentType.AUTOMATIC_DEBIT_FORMAT);
        break;
      default:
        break;
    }
  };

  const automaticDebitMenuOptions = [
    {
      name: AutomaticDebitMenuActions.SEND_TO_SIGN_AUTOMATIC_DEBIT_REQUEST,
      label: 'COMMON.SEND_TO_SIGN',
      disabled: !shouldShowSendToSignAutomaticDebitRequest(contract),
      icon: <SendIcon />
    },
    {
      name: AutomaticDebitMenuActions.HISTORY_AUTOMATIC_DEBIT_REQUEST,
      label: 'COMMON.HISTORY',
      disabled: !(contract.automaticDebitRequest?.document),
      icon: <ListAltIcon />
    }
  ];

  return (
    <>
      <DetailRow
        name={
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Box component="div" className={classes.title}>
              {t('CONTRACT_VIEW.CONTRACT_PDF')}
            </Box>
            <Box sx={{ marginLeft: 1 }}>
              {getContractSignedStatus(contract)}
            </Box>
          </Box>
        }
        value={
          <DocumentLink
            link={
              contract.contractDoc ? contract.contractDoc.documentUrl : null
            }
            fileName={
              `Contrato ${contract.clientName}, folio ${contract.folio}` ?? null
            }
          />
        }
      >
        {contract.status !== ContractStatus.SIGNED && (
          <DocumentsContractMenu
            documentType={DocumentType.CONTRACT}
            contract={contract}
            contractId={contract.id}
            documents={Boolean(contract.contractDoc)}
          />
        )}
      </DetailRow>
      {contract.promissoryNote && !contract.cessionPromissoryNote && (
        <DetailRow
          name={
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              <Box component="div" className={classes.title}>
                {t('CONTRACT_VIEW.PROMISSORY_NOTE')}
              </Box>
              <Box sx={{ marginLeft: 1 }}>
                {getPromissoryNoteSignStatus(contract.promissoryNote)}
              </Box>
            </Box>
          }
          value={
            <DocumentLink
              link={contract.promissoryNote?.document?.documentUrl}
            />
          }
        >
          <DocumentsContractMenu
            documentType={DocumentType.PROMISSORY_NOTE}
            promissoryNoteId={contract.promissoryNote?.id}
            contractId={contract.id}
            contract={contract}
            documents={Boolean(contract.promissoryNote?.document)}
          />
        </DetailRow>
      )}

      {contract.cessionPromissoryNote && (
        <DetailRow
          name={
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              <Box component="div" className={classes.title}>
                {t('CONTRACT_VIEW.PROMISSORY_NOTE')}
              </Box>
              <Box sx={{ marginLeft: 1 }}>
                {getPromissoryNoteSignStatus(contract.cessionPromissoryNote)}
              </Box>
            </Box>
          }
          value={
            <DocumentLink
              link={contract.cessionPromissoryNote.document.documentUrl}
            />
          }
        >
          <DocumentsContractMenu
            documentType={DocumentType.PROMISSORY_NOTE}
            promissoryNoteId={contract.cessionPromissoryNote.id}
            contractId={contract.id}
            contract={contract}
            documents={Boolean(contract.cessionPromissoryNote.document)}
          />
        </DetailRow>
      )}

      {contract.lastCapitalContribution && (
        <DetailRow
          name={
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              <Box component="div" className={classes.title}>
                {t('COMMON.AMENDING_AGREEMENT')}
              </Box>
              <Box sx={{ marginLeft: 1 }}>
                {getAmendingAgreementSignStatus(contract)}
              </Box>
            </Box>
          }
          value={
            <DocumentLink
              link={
                contract.lastCapitalContribution.document &&
                contract.lastCapitalContribution.document.documentUrl
              }
            />
          }
        >
          <DocumentsMenu
            contractId={contract.id}
            documentType={DocumentType.AMENDING_AGREEMENT}
            loadAmendingAgreements
            documents={contract.lastCapitalContribution.document}
            isDisabledAdd
          />
        </DetailRow>
      )}

      {contract?.contractCession?.document && (
        <DetailRow
          name={
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              <Box component="div" className={classes.title}>
                {t('CONTRACT_VIEW.CONTRACT_CESSION')}
              </Box>
              <Box sx={{ marginLeft: 1 }}>
                {getContractCessionSignStatus(contract)}
              </Box>
            </Box>
          }
          value={
            <DocumentLink
              link={contract?.contractCession?.document?.documentUrl}
            />
          }
        />
      )}

      {contract?.contractEarlyTermination?.document && (
        <>
          <DetailRow
            name={
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Box component="div" className={classes.title}>
                  {t('CONTRACT_VIEW.CONTRACT_EARLY_TERMINATION')}
                </Box>
                <Box sx={{ marginLeft: 1 }}>
                  {getContractEarlyTerminationSignStatus(contract)}
                </Box>
              </Box>
            }
            value={
              <DocumentLink
                link={contract?.contractEarlyTermination?.document?.documentUrl}
              />
            }
          />

          <DetailRow
            name={
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Box component="div" className={classes.title}>
                  {t('CONTRACT_VIEW.EQUIPMENT_DELIVERY_ACT')}
                </Box>
              </Box>
            }
            value={
              <DocumentLink
                link={contract?.equipmentDeliveryAct?.documentUrl}
              />
            }
          >
            <DocumentsMenu
              documentType={DocumentType.EQUIPMENT_DELIVERY_ACT}
              clientId={contract.clientId}
              contractId={contract.id}
              contractEarlyTerminationId={
                contract?.contractEarlyTermination?.id
              }
              documents={Boolean(contract?.equipmentDeliveryAct)}
              allowExcelUpload={false}
            />
          </DetailRow>
        </>
      )}

      {contract.automaticDebitRequest && (
        <DetailRow
          name={
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              <Box component="div" className={classes.title}>
                {t('CONTRACT_VIEW.AUTOMATIC_DEBIT_REQUEST_DOC')}
              </Box>
              <Box sx={{ marginLeft: 1 }}>
                {getAutomaticDebitRequestSignStatus(contract)}
              </Box>
            </Box>
          }
          value={
            <DocumentLink
              link={contract.automaticDebitRequest.document.documentUrl}
            />
          }
        >
          <MoreMenu
            deleteDisabled
            editDisabled
            options={automaticDebitMenuOptions}
            handleClickOption={handleOpenActionsAutomaticDebitRequest}
          />
          {contract.automaticDebitRequest &&
            contract.automaticDebitRequest.signStatus ===
              AutomaticDebitRequestSignStatus.DRAFT && (
              <ConfirmationDialog
                open={openConfirmationDialog}
                handleClose={handleCloseAutomaticDebitRequestDialog}
                title={t(
                  'CONTRACT_VIEW.SEND_TO_SIGN_AUTOMATIC_DEBIT_REQUEST_DOC_TITLE'
                )}
                description={t(
                  'CONTRACT_VIEW.SEND_TO_SIGN_AUTOMATIC_DEBIT_REQUEST_DOC_DESCRIPTION'
                )}
                onSubmit={sendToSignAutomaticDebitRequest}
                isLoading={isLoading}
              />
            )}
          <DocumentListModal
            title={DocumentType.AUTOMATIC_DEBIT_FORMAT}
            open={openAutomaticDebitHistory}
            contractId={contract.id}
            documentType={DocumentType.AUTOMATIC_DEBIT_FORMAT}
            handleClose={() => setOpenAutomaticDebitHistory(null)}
          />
        </DetailRow>
      )}

      {contract.terminationLetterDoc && (
        <>
          <DetailRow
            name={
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Box component="div" className={classes.title}>
                  {t('COMMON.TERMINATION_LETTER')}
                </Box>
              </Box>
            }
            value={
              <DocumentLink link={contract.terminationLetterDoc.documentUrl} />
            }
          >
            <TerminationLetterDocumentMenu contractId={contract.id} />
          </DetailRow>
        </>
      )}
    </>
  );
};

DocumentDetail.propTypes = {
  contract: PropTypes.object.isRequired
};

export default DocumentDetail;
